<template>
  <div class="home">
    <div class="content" v-if="!isLoading">
      <div>
        <navbar></navbar>
        <header-item></header-item>
        <hero :heroTitle="title" :heroDescription="description" :backgroundImageUrl="image" :heroVideo="video"></hero>
        <DiplomasSection />
        <CommonCourses></CommonCourses>
        <div class="position-relative">
          <div class="svg-bg"></div>
          <div class="svg-bg-1"></div>
            <brief :whoWeAre="WhoWeAre"></brief>
            <courses-box></courses-box>

            <AdvantageBox></AdvantageBox>
          </div>
        <BlogsSlider />
        <features :countings="countings"></features>
        <TeamSection />
        <CourseRating />
        <!-- <ProfileCard :slides="customers"></ProfileCard> -->
        <CertificateSection :Certificates="certificates"></CertificateSection>
        <shop-by :opacityApplied="true"></shop-by>
      </div>
      <footer-item></footer-item>
    </div>

    <div class="d-flex justify-content-center align-items-center vh-100 w-100" v-else>
      <loader></loader>
    </div>
  </div>
</template>

<script setup>
import { defineAsyncComponent } from 'vue'

// Eager components
import Navbar from '@/components/Layout/Navbar.vue'
import HeaderItem from '@/components/Layout/Menu.vue'
import Hero from '@/components/Layout/Hero.vue'
import FooterItem from '@/components/Layout/Footer.vue'
import Loader from '@/components/Common/Loader.vue'
import CourseRating from '@/components/SingleCourse/CourseRating.vue'
// Lazy components
const CommonCourses = defineAsyncComponent(() => import('@/components/Homepage/CommonCourses.vue'))
const Brief = defineAsyncComponent(() => import('@/components/Homepage/brief.vue'))
const CoursesBox = defineAsyncComponent(() => import('@/components/Homepage/CoursesBox.vue'))
const DiplomasSection = defineAsyncComponent(() => import('@/components/Homepage/DiplomasSection.vue'))
const AdvantageBox = defineAsyncComponent(() => import('@/components/Homepage/Advantages.vue'))
const BlogsSlider = defineAsyncComponent(() => import('@/components/Homepage/BlogsSlider.vue'))
const Features = defineAsyncComponent(() => import('@/components/Homepage/Features.vue'))
const TeamSection = defineAsyncComponent(() => import('@/components/Homepage/TeamSection.vue'))
//const CourseRating = defineAsyncComponent(() => import('@/components/SingleCourse/CourseRating.vue'))
const CertificateSection = defineAsyncComponent(() => import('@/components/Homepage/Certificates.vue'))
const ShopBy = defineAsyncComponent(() => import('@/components/About/ShopBy.vue'))
// const ProfileCard = defineAsyncComponent(() => import('@/components/Common/ProfileCard.vue'))
import { ref, reactive, onMounted } from 'vue';
import { useHead } from '@vueuse/head';
import { getPageByCode } from "../Services/api/PagesService";
import PagesCode from "@/constants/PagesCode.js";
import { getSeoByCode } from "@/Services/api/SeoService";
import SeoCodes from "@/constants/SeoCode";
import { useStore } from 'vuex';
import { computed ,watch} from 'vue';

const store = useStore();
// Access Vuex getter as a computed ref
const currentLanguage = computed(() => store.getters.currentLanguage);
// Primitive values
const isLoading = ref(false);
const countings = ref(null);
const title = ref('');
const description = ref('');
const image = ref('');
const video = ref('');

// Object values
const certificates = reactive({});
const WhoWeAre = reactive({});
const metaObject = reactive({});

// Fetch Page Data
const fetchPageData = async () => {
  isLoading.value = true;
  try {
    const data = await getPageByCode(PagesCode.Home);
    const response = data.data;

    title.value = response.title;
    description.value = response.description;
    image.value = response.image;
    video.value = response.video;

    store.commit('setHomeVideo',response.video)

    Object.assign(WhoWeAre, response.homeWhoAreWe);
    countings.value = response.countings;
    Object.assign(certificates, response.certificates);
  } catch (error) {
    console.error('Error fetching page data:', error.message);
  } finally {
    isLoading.value = false;
  }
};

// SEO & Head Meta Update
const updateHeadMeta = () => {
  const headTitle = metaObject.metaTitle;
  const headDescription = metaObject.metaDescription;
  const keywordsArray = metaObject.keywords;
  const keywords =
    keywordsArray && keywordsArray.some((kw) => kw.trim() !== '')
      ? keywordsArray.join(', ')
      : 'مجتمع, تعليم, تطوير شخصي, تبادل المعرفة, إبتكار';

  useHead({
    title: headTitle,
    meta: [
      { name: 'description', content: headDescription },
      { name: 'keywords', content: keywords },
      { name: 'robots', content: 'index, follow' },
      { name: 'author', content: 'معهد ابتكار المعرفة العالي للتدريب' },
      { name: 'language', content: currentLanguage.value === 'ar' ? 'ar' : 'en' },

      // Open Graph
      { property: 'og:title', content: headTitle },
      { property: 'og:description', content: headDescription },
      { property: 'og:image', content: 'https://example.com/homepage-image.jpg' },
      { property: 'og:url', content: 'https://example.com' },
      { property: 'og:type', content: 'website' },

      // Twitter Card
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: headTitle },
      { name: 'twitter:description', content: headDescription },
      { name: 'twitter:image', content: 'https://example.com/homepage-image.jpg' },

      // Structured Data
      {
        name: 'structuredData',
        content: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: headTitle,
          description: headDescription,
          url: 'https://example.com',
          publisher: {
            '@type': 'Organization',
            name: 'Ebtkar Community',
            logo: {
              '@type': 'ImageObject',
              url: 'https://example.com/logo.jpg',
            },
          },
        }),
      },
    ],
  });
};

// Fetch SEO Data
const fetchSeoData = async () => {
  try {
    const response = await getSeoByCode(SeoCodes.Home);
    Object.assign(metaObject, response.data);
    updateHeadMeta();
  } catch (error) {
    console.error('Error fetching SEO data:', error);
  }
};

watch(
  currentLanguage,
  (newLang, oldLang) => {
    fetchPageData();
    fetchSeoData();
  }
);

// Optionally call in onMounted
onMounted(() => {
  fetchPageData();
  fetchSeoData();
 // store.dispatch('fetchClients');
});
</script>